import type { ReferenceTabRoute, ReferenceTabRouteName } from "#info-panel-tab-routes";
import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import type { ResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";

export type MorphemeInfoPanelPresentation = "embedded" | "expo-route";

function infoBackCardScrollKey(infoBackCardSlice: InfoMorphemeSlice): string {
  return infoBackCardSlice.morphemeType === "grapheme"
    ? `${infoBackCardSlice.morphemeType}:${infoBackCardSlice.grapheme}`
    : `${infoBackCardSlice.morphemeType}:${infoBackCardSlice.word}`;
}

export function useMorphemeInfoPanelScrollState(infoBackCardSlice: InfoMorphemeSlice) {
  const [isScrolled, setIsScrolled] = useState(false);

  const scrollKey = infoBackCardScrollKey(infoBackCardSlice);
  useEffect(() => {
    setIsScrolled(false);
  }, [scrollKey]);

  return { isScrolled, setIsScrolled };
}

export function useMorphemeInfoPanelContextValue({
  floatingTabBottomInset,
  infoBackCardSlice,
  initialReferenceTab,
  isScrolled,
  morphemeHrefObject,
  setIsScrolled,
  tabRouteList,
}: {
  floatingTabBottomInset: number;
  /** Narrowed by the Info screen that mounted this panel (it rendered `MorphemeNotFound` for null). */
  infoBackCardSlice: ResolvedQuery<InfoMorphemeSlice | null>;
  initialReferenceTab?: ReferenceTabRouteName;
  isScrolled: boolean;
  morphemeHrefObject: MorphemeHrefObject;
  setIsScrolled: Dispatch<SetStateAction<boolean>>;
  tabRouteList?: readonly ReferenceTabRoute[];
}) {
  return useMemo(
    () => ({
      floatingTabBottomInset,
      infoBackCardSlice,
      initialReferenceTab,
      isScrolled,
      morphemeHrefObject,
      setIsScrolled,
      tabRouteList,
    }),
    [
      morphemeHrefObject,
      infoBackCardSlice,
      floatingTabBottomInset,
      initialReferenceTab,
      isScrolled,
      setIsScrolled,
      tabRouteList,
    ],
  );
}

export type MorphemeInfoPanelContextValue = ReturnType<typeof useMorphemeInfoPanelContextValue>;
